import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, formatDateTime, formatNumber } from '../api';
import { usePolling } from '../auth';
import CandleChart, { type ChartHighlight, type ChartMarker } from '../components/CandleChart';
import NewsCheckLine from '../components/NewsCheckLine';
import RuleTraceView, { isRuleTrace, ScoreTable } from '../components/RuleTrace';
import { ActionBadge, Badge, Empty, Note, Panel, Spinner } from '../components/ui';
import type { AnalysisResponse, ChartDecision, ChartResponse, UniverseEntry } from '../types';

/**
 * Daily candles for one instrument with every decision placed on the bar it was
 * made on. Selecting a decision shows the rule that fired as checks (value
 * against threshold), the score as the sum of its signals, and draws on the chart
 * what the rule read: the bars behind each signal, the levels they rest on, and
 * the stops and entry the decision set.
 */

const RANGES = [
  { bars: 65, label: '3M' },
  { bars: 130, label: '6M' },
  { bars: 260, label: '1Y' },
  { bars: 520, label: '2Y' },
] as const;
const DEFAULT_BARS = 130;

const STREAM_VERB: Record<string, string> = { BUY: 'bought', SELL: 'sold', HOLD: 'proposed' };

/** Which way a decision pointed, for its marker: up for a buy or cover, down for a sale or short. */
function sideOf(decision: ChartDecision): 'up' | 'down' | null {
  if (decision.action === 'BUY') return 'up';
  if (decision.action === 'SELL') return 'down';
  const outcome = isRuleTrace(decision.ruleTrace) ? decision.ruleTrace.outcome : undefined;
  if (outcome === 'open_long' || outcome === 'close_short' || outcome === 'stop_short') return 'up';
  if (outcome === 'open_short' || outcome === 'close_long' || outcome === 'stop_long') return 'down';
  return null;
}

function statusOf(decision: ChartDecision): { label: string; tone: 'green' | 'amber' | 'red' | 'grey' } {
  if (decision.accepted) return { label: 'executed', tone: 'green' };
  const verdict = decision.aiVotes?.verdict;
  if (verdict === 'VETO') return { label: 'vetoed by the model', tone: 'red' };
  if (decision.action === 'HOLD') return { label: 'not confirmed', tone: 'amber' };
  const news = decision.newsCheck?.verdict;
  if (news === 'BLOCK' || news === 'DELAY' || news === 'HOLD' || news === 'OVERRIDE') {
    return { label: 'held by the news check', tone: 'amber' };
  }
  return { label: 'not executed', tone: 'amber' };
}

const shortDate = (date: string): string =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export default function Charts() {
  const params = useParams<{ symbol?: string }>();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const symbol = (params.symbol ?? 'AAPL').toUpperCase();
  const requestedBars = Number(search.get('bars'));
  const bars = RANGES.some((range) => range.bars === requestedBars) ? requestedBars : DEFAULT_BARS;
  const wantedDecision = Number(search.get('decision')) || null;
  const wantedTrade = Number(search.get('trade')) || null;

  const universe = usePolling<{ universe: UniverseEntry[] }>(() => api.universe(), null);
  const chart = usePolling<ChartResponse>(() => api.chart(symbol, bars), 60_000, [symbol, bars]);
  const analysis = usePolling<AnalysisResponse>(() => api.analysis(symbol), null, [symbol]);
  // Polling keeps the previous symbol's data until the new response lands.
  const data = chart.data && chart.data.symbol === symbol && chart.data.bars.length > 0 ? chart.data : null;
  const indicators = analysis.data && analysis.data.symbol === symbol ? analysis.data : null;

  const [input, setInput] = useState(symbol);
  const [streamFilter, setStreamFilter] = useState<number | 'all'>('all');
  const [showAverages, setShowAverages] = useState(true);
  const [showBands, setShowBands] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => setInput(symbol), [symbol]);
  useEffect(() => setSelectedId(null), [symbol]);

  const indexByDate = useMemo(() => new Map((data?.bars ?? []).map((bar, index) => [bar.date, index])), [data]);
  const indexAtOrAfter = (date: string | undefined): number | null => {
    if (!data || !date) return null;
    const exact = indexByDate.get(date);
    if (exact !== undefined) return exact;
    const found = data.bars.findIndex((bar) => bar.date >= date);
    return found === -1 ? null : found;
  };

  // A decision or trade named in the address is opened, widening the range if
  // it is older than the bars on show.
  useEffect(() => {
    if (!data || (!wantedDecision && !wantedTrade)) return;
    const wanted = data.decisions.find((decision) =>
      wantedDecision ? decision.id === wantedDecision : decision.tradeId === wantedTrade,
    );
    if (wanted && wanted.barDate) {
      setSelectedId(wanted.id);
      return;
    }
    const wider = RANGES.find((range) => range.bars > bars);
    if (wider) {
      setSearch(
        (previous) => {
          previous.set('bars', String(wider.bars));
          return previous;
        },
        { replace: true },
      );
    }
  }, [data, wantedDecision, wantedTrade, bars, setSearch]);

  // A proposal that keeps failing (say, on the daily trade limit) is re-made
  // every cycle; those repeats collapse into one entry per stream, bar and side,
  // shown as the latest of them with a count.
  const { decisions, repeats, representative } = useMemo(() => {
    const kept: ChartDecision[] = [];
    const counts = new Map<number, number>();
    const firstOf = new Map<string, number>();
    const groupOf = new Map<number, number>();
    for (const decision of data?.decisions ?? []) {
      if (streamFilter !== 'all' && decision.streamId !== streamFilter) continue;
      if (decision.accepted) {
        kept.push(decision);
        continue;
      }
      const key = `${decision.streamId}|${decision.barDate}|${decision.action}|${sideOf(decision)}`;
      const first = firstOf.get(key);
      if (first === undefined) {
        firstOf.set(key, decision.id);
        counts.set(decision.id, 1);
        kept.push(decision);
      } else {
        counts.set(first, (counts.get(first) ?? 1) + 1);
        groupOf.set(decision.id, first);
      }
    }
    return { decisions: kept, repeats: counts, representative: groupOf };
  }, [data, streamFilter]);
  const streams = useMemo(() => {
    const seen = new Map<number, string>();
    for (const decision of data?.decisions ?? []) seen.set(decision.streamId, decision.streamName);
    return [...seen.entries()];
  }, [data]);

  // With nothing chosen, the most recent decision is shown. A repeat that was
  // folded into its group opens as that group.
  const shownId = selectedId === null ? null : (representative.get(selectedId) ?? selectedId);
  const selected =
    decisions.find((decision) => decision.id === shownId) ??
    (selectedId === null ? decisions.find((decision) => decision.barDate) : undefined) ??
    null;

  const markers: ChartMarker[] = decisions.flatMap((decision) => {
    const side = sideOf(decision);
    const index = decision.barDate ? indexByDate.get(decision.barDate) : undefined;
    if (side === null || index === undefined) return [];
    return [
      {
        id: decision.id,
        index,
        side,
        executed: decision.accepted,
        title:
          `${decision.streamName} ${decision.accepted ? (STREAM_VERB[decision.action] ?? 'proposed') : 'proposed to ' + (decision.action === 'SELL' ? 'sell' : 'buy')} ${symbol} on ` +
          `${decision.barDate ? shortDate(decision.barDate) : '?'}: ${statusOf(decision).label}` +
          ((repeats.get(decision.id) ?? 1) > 1 ? ` (${repeats.get(decision.id)} times)` : ''),
      },
    ];
  });

  const highlight: ChartHighlight | null = (() => {
    if (!selected || !selected.barDate) return null;
    const index = indexByDate.get(selected.barDate);
    if (index === undefined) return null;
    const trace = isRuleTrace(selected.ruleTrace) ? selected.ruleTrace : null;
    const counted = (trace?.score?.signals ?? []).filter((signal) => signal.counted && signal.to).slice(0, 4);
    return {
      index,
      levels: (trace?.levels ?? []).filter((level) => typeof level.price === 'number'),
      spans: counted.map((signal) => ({
        from: indexAtOrAfter(signal.from ?? signal.to) ?? 0,
        to: indexAtOrAfter(signal.to) ?? index,
        label: signal.name,
        direction: signal.direction,
        levels: signal.levels ?? [],
      })),
      fillPrice: selected.fillPrice,
      tracedScore: typeof trace?.score?.score === 'number' ? trace.score.score : null,
    };
  })();

  const thresholdKind = selected?.streamKind ?? (streamFilter === 'all' ? null : decisions[0]?.streamKind ?? null);
  const thresholds = data ? (thresholdKind === 'HYBRID' ? data.thresholds.hybrid : data.thresholds.pattern) : null;
  const provisionalIndex = data?.provisionalDate ? (indexByDate.get(data.provisionalDate) ?? null) : null;

  function select(id: number): void {
    setSelectedId(id);
    setSearch(
      (previous) => {
        previous.delete('trade');
        previous.set('decision', String(id));
        return previous;
      },
      { replace: true },
    );
  }

  function openSymbol(next: string): void {
    const clean = next.trim().toUpperCase();
    if (!/^[A-Z0-9.\-]{1,20}$/.test(clean)) return;
    navigate(`/charts/${encodeURIComponent(clean)}${bars === DEFAULT_BARS ? '' : `?bars=${bars}`}`);
  }

  function setRange(next: number): void {
    setSearch(
      (previous) => {
        previous.set('bars', String(next));
        return previous;
      },
      { replace: true },
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Charts</h1>
          <p>
            Daily candles with every decision on the bar it was made. Select a marker to see the rule that
            fired: each check as the value against what it needed, the pattern score as the sum of its
            signals, and on the chart the bars those signals were read from and the stops the trade set.
          </p>
        </div>
        <button className="btn" type="button" onClick={chart.reload}>
          Refresh
        </button>
      </div>

      <Panel title="Instrument">
        <div className="inline-form">
          <form
            className="inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              openSymbol(input);
            }}
          >
            <div className="field" style={{ maxWidth: 220 }}>
              <label htmlFor="chart-symbol">Symbol</label>
              <input
                id="chart-symbol"
                type="text"
                list="chart-universe"
                value={input}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setInput(event.target.value)}
              />
              <datalist id="chart-universe">
                {(universe.data?.universe ?? []).map((entry) => (
                  <option key={entry.symbol} value={entry.symbol}>
                    {entry.name}
                  </option>
                ))}
              </datalist>
            </div>
            <button className="btn btn-primary" type="submit">
              Open
            </button>
          </form>
          <div className="field">
            <span className="field-label" id="chart-range-label">
              Range
            </span>
            <div className="tabs" role="group" aria-labelledby="chart-range-label">
              {RANGES.map((range) => (
                <button
                  key={range.bars}
                  type="button"
                  className={`tab${range.bars === bars ? ' active' : ''}`}
                  aria-pressed={range.bars === bars}
                  onClick={() => setRange(range.bars)}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
          <div className="field" style={{ maxWidth: 200 }}>
            <label htmlFor="chart-stream">Decisions from</label>
            <select
              id="chart-stream"
              value={streamFilter}
              onChange={(event) => setStreamFilter(event.target.value === 'all' ? 'all' : Number(event.target.value))}
            >
              <option value="all">All streams</option>
              {streams.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <label className="check">
            <input type="checkbox" checked={showAverages} onChange={(event) => setShowAverages(event.target.checked)} />
            averages 20 / 50 / 200
          </label>
          <label className="check">
            <input type="checkbox" checked={showBands} onChange={(event) => setShowBands(event.target.checked)} />
            Bollinger band
          </label>
        </div>
      </Panel>

      {chart.error && !data && (
        <Note tone="warn" title={`No chart for ${symbol}`}>
          {chart.error}
        </Note>
      )}
      {!data && !chart.error && <Spinner label={`Loading ${symbol}`} />}

      {data && (
        <>
          <Panel
            title={`${data.symbol} · ${data.name}`}
            note={
              `${data.exchange} · ${data.bars.length} daily bars in ${data.currency} via ${data.provider}` +
              (data.provisionalDate ? ` · ${shortDate(data.provisionalDate)} still trading` : '')
            }
          >
            <CandleChart
              bars={data.bars}
              overlays={data.overlays}
              showAverages={showAverages}
              showBands={showBands}
              thresholds={thresholds}
              markers={markers}
              selectedId={selected?.id ?? null}
              onSelect={select}
              highlight={highlight}
              provisionalIndex={provisionalIndex}
              label={`Daily candles for ${data.symbol} with ${markers.length} decisions marked`}
            />
            <div className="cc-legend mono-small">
              <span>
                <span className="cc-key cc-key-up">▲</span> buy or cover
              </span>
              <span>
                <span className="cc-key cc-key-down">▼</span> sell or short
              </span>
              <span>filled: executed · outline: proposed, not executed</span>
              {showAverages && (
                <span>
                  <span className="cc-swatch cc-sma20" /> 20 <span className="cc-swatch cc-sma50" /> 50{' '}
                  <span className="cc-swatch cc-sma200" /> 200-day
                </span>
              )}
              <span>
                score lines: {thresholdKind === 'HYBRID' ? 'Hybrid' : 'Pattern'} stream thresholds
              </span>
            </div>
          </Panel>

          <div className="split-64">
            <Panel title="Decision" note={selected ? `${selected.streamName} · ${formatDateTime(selected.createdAt)}` : undefined}>
              {selected ? <DecisionDetail decision={selected} data={data} /> : <Empty>No decisions on this range.</Empty>}
            </Panel>

            <Panel title="Decisions on this chart" note={`${decisions.length} shown`}>
              {decisions.length === 0 ? (
                <Empty>
                  No stream has proposed a trade in {data.symbol} over this range. Holds are not marked: they
                  repeat every cycle.
                </Empty>
              ) : (
                <ul className="chart-decisions">
                  {decisions.map((decision) => {
                    const status = statusOf(decision);
                    const trace = isRuleTrace(decision.ruleTrace) ? decision.ruleTrace : null;
                    return (
                      <li key={decision.id}>
                        <button
                          type="button"
                          className={`chart-decision${decision.id === selected?.id ? ' active' : ''}`}
                          aria-pressed={decision.id === selected?.id}
                          onClick={() => select(decision.id)}
                        >
                          <span className="mono-small">{decision.barDate ? shortDate(decision.barDate) : 'before range'}</span>
                          <ActionBadge action={decision.action} />
                          <span>{decision.streamName}</span>
                          <Badge tone={status.tone}>{status.label}</Badge>
                          {(repeats.get(decision.id) ?? 1) > 1 && (
                            <span className="mono-small">proposed {repeats.get(decision.id)}× that day</span>
                          )}
                          <span className="chart-decision-why">
                            {trace?.headline ?? decision.rationale?.split(' || ')[0] ?? ''}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>
          </div>

          <div className="grid grid-2">
            <Panel title="Score on the latest bar" note="what the rules would read now">
              {data.latest ? (
                <ScoreTable
                  score={data.latest}
                  threshold={{ label: 'Pattern buy', value: data.thresholds.pattern.buy }}
                />
              ) : (
                <Empty>Not enough history to score.</Empty>
              )}
            </Panel>
            <Panel title="Indicators on the latest bar">
              {indicators ? <IndicatorList data={indicators} /> : <Spinner label="Loading indicators" />}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}

function DecisionDetail({ decision, data }: { decision: ChartDecision; data: ChartResponse }) {
  const status = statusOf(decision);
  const trace = isRuleTrace(decision.ruleTrace) ? decision.ruleTrace : null;
  const thresholds = decision.streamKind === 'HYBRID' ? data.thresholds.hybrid : data.thresholds.pattern;
  const outcome = trace?.outcome;
  const threshold =
    outcome === 'open_long'
      ? { label: 'buy', value: thresholds.buy }
      : outcome === 'open_short'
        ? { label: 'short', value: thresholds.short }
        : outcome === 'close_long'
          ? { label: 'sell', value: thresholds.sell }
          : outcome === 'close_short'
            ? { label: 'cover', value: thresholds.cover }
            : undefined;
  const [reason, filled] = (decision.rationale ?? '').split(' || Filled: ');
  const model = decision.aiVotes;
  const news = decision.newsCheck && decision.newsCheck.verdict !== 'NO_NEWS' ? decision.newsCheck : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <ActionBadge action={decision.action} />
        <Badge tone="grey">{decision.streamName}</Badge>
        <Badge tone={status.tone}>{status.label}</Badge>
        {decision.fillPrice !== null && (
          <span className="mono-small">
            filled at {formatNumber(decision.fillPrice, 2)} {data.currency}
          </span>
        )}
      </div>

      {trace ? (
        <RuleTraceView trace={trace} {...(threshold ? { thresholdLabel: threshold } : {})} />
      ) : (
        <Note tone="info" title={decision.streamKind === 'AI' ? 'Decided by the model' : 'Recorded before rule traces'}>
          {decision.streamKind === 'AI'
            ? 'The AI stream has no fixed rule: the model weighed the briefing and gave the reason below. Only its stop-losses are mechanical, and those carry a trace.'
            : 'This decision was logged before each rule’s checks were stored, so only its written reason is available. New decisions show every check.'}
        </Note>
      )}

      {model && (model.verdict || model.reason) && (
        <section className="trace-section">
          <h4>Model vote</h4>
          <p className="reason" style={{ margin: 0 }}>
            {model.verdict && <b style={{ color: 'var(--ink)' }}>{model.verdict}</b>}
            {typeof model.confidence === 'number' ? ` (confidence ${model.confidence.toFixed(2)})` : ''}
            {model.model ? ` · ${model.model}` : ''}
            {model.reason ? `: ${model.reason}` : ''}
          </p>
        </section>
      )}

      {news && (
        <section className="trace-section">
          <h4>News check</h4>
          <NewsCheckLine check={news} />
        </section>
      )}

      <section className="trace-section">
        <h4>Logged reason</h4>
        <p className="reason" style={{ margin: 0 }}>
          {reason || 'No reason recorded.'}
        </p>
        {filled && (
          <p className="reason" style={{ margin: '4px 0 0' }}>
            Filled: {filled}
          </p>
        )}
        {decision.rejectReason && (
          <p className="reason" style={{ margin: '4px 0 0', color: 'var(--amber)' }}>
            Not executed: {decision.rejectReason}
          </p>
        )}
      </section>
    </div>
  );
}

function IndicatorList({ data }: { data: AnalysisResponse }) {
  const ind = data.indicators;
  return (
    <dl className="kv">
      <dt>Close</dt>
      <dd>
        {formatNumber(ind.close, 4)} {data.currency}
      </dd>
      <dt>Trend</dt>
      <dd>{ind.trend}</dd>
      <dt>SMA 20 / 50 / 200</dt>
      <dd>
        {formatNumber(ind.sma20, 2)} / {formatNumber(ind.sma50, 2)} / {formatNumber(ind.sma200, 2)}
      </dd>
      <dt>RSI (14)</dt>
      <dd>{ind.rsi14 === null ? '—' : ind.rsi14.toFixed(1)}</dd>
      <dt>MACD histogram</dt>
      <dd>{ind.macd ? ind.macd.histogram.toFixed(4) : '—'}</dd>
      <dt>Bollinger</dt>
      <dd>
        {ind.bollinger
          ? `${ind.bollinger.lower.toFixed(2)} – ${ind.bollinger.upper.toFixed(2)} (%B ${ind.bollinger.percentB.toFixed(2)})`
          : '—'}
      </dd>
      <dt>ATR (14)</dt>
      <dd>
        {ind.atr14 === null ? '—' : ind.atr14.toFixed(2)}
        {ind.atrPct !== null && ` (${ind.atrPct.toFixed(2)}% of price)`}
      </dd>
      <dt>ADX (14)</dt>
      <dd>
        {ind.adx14 === null ? '—' : ind.adx14.toFixed(1)}
        {ind.plusDi !== null && ind.minusDi !== null && ` · +DI ${ind.plusDi.toFixed(1)} / −DI ${ind.minusDi.toFixed(1)}`}
      </dd>
      <dt>Volume vs 20-day</dt>
      <dd>{ind.volumeSurge === null ? '—' : `${ind.volumeSurge.toFixed(2)}×`}</dd>
      <dt>52-week range</dt>
      <dd>
        {formatNumber(ind.low52Week, 2)} – {formatNumber(ind.high52Week, 2)}
        {ind.pctFrom52WeekHigh !== null && ` (${ind.pctFrom52WeekHigh.toFixed(1)}% from the high)`}
      </dd>
      <dt>Returns 5 / 20 / 60 days</dt>
      <dd>
        {formatNumber(ind.return5d, 1)}% / {formatNumber(ind.return20d, 1)}% / {formatNumber(ind.return60d, 1)}%
      </dd>
    </dl>
  );
}
